import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { useAdmin } from '@/contexts/AdminContext';
import AdminLayout from './AdminLayout';
import { BookOpen, Share2, MessageSquare, Trash2, RefreshCw, ChevronDown, ChevronUp, Calendar, Loader2, Eye, TrendingUp, Tags } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';
import { toast } from 'sonner';

function StatBadge({ icon: Icon, label, value, color, isDark, colors }) {
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: isDark ? 'rgba(30,41,59,0.5)' : '#fff',
        border: `1px solid ${colors.cardBorder}`,
      }}
    >
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: colors.textMuted }}>{label}</span>
      </div>
      <p className="text-2xl font-bold" style={{ color: colors.text }}>{value}</p>
    </div>
  );
}

function EngagementChart({ isDark, colors }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  const fetchAnalytics = async (days) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reflections/analytics?days=${days}`, { withCredentials: true });
      setData(res.data);
    } catch {
      // Silently fail — chart is supplementary
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnalytics(range); }, [range]);

  const tooltipStyle = {
    backgroundColor: isDark ? '#1e293b' : '#fff',
    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
    borderRadius: 10,
    fontSize: 12,
    color: colors.text,
  };

  const formatDate = (d) => {
    const [, m, day] = d.split('-');
    return `${m}/${day}`;
  };

  return (
    <div
      className="rounded-2xl p-5"
      style={{
        background: isDark ? 'rgba(30,41,59,0.5)' : '#fff',
        border: `1px solid ${colors.cardBorder}`,
      }}
      data-testid="reflection-engagement-chart"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#6366f118' }}>
            <TrendingUp className="h-4 w-4" style={{ color: '#6366f1' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: colors.text }}>
            Engagement Over Time
          </h3>
        </div>
        <div className="flex gap-1 rounded-lg p-0.5" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f1f5f9' }}>
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setRange(d)}
              className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
              style={{
                background: range === d ? (isDark ? '#334155' : '#fff') : 'transparent',
                color: range === d ? colors.text : colors.textMuted,
                boxShadow: range === d ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
              }}
              data-testid={`range-${d}d-btn`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: colors.textMuted }} />
        </div>
      ) : !data ? (
        <p className="text-sm text-center py-12" style={{ color: colors.textMuted }}>Unable to load analytics</p>
      ) : (
        <>
          {/* Summary row */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="rounded-lg p-3 text-center" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
              <p className="text-xs" style={{ color: colors.textMuted }}>Views</p>
              <p className="text-lg font-bold" style={{ color: '#14b8a6' }}>{data.totals.views}</p>
            </div>
            <div className="rounded-lg p-3 text-center" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
              <p className="text-xs" style={{ color: colors.textMuted }}>Shares</p>
              <p className="text-lg font-bold" style={{ color: '#6366f1' }}>{data.totals.shares}</p>
            </div>
            <div className="rounded-lg p-3 text-center" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
              <p className="text-xs" style={{ color: colors.textMuted }}>Discussions</p>
              <p className="text-lg font-bold" style={{ color: '#8b5cf6' }}>{data.totals.discussions}</p>
            </div>
          </div>

          {/* Chart */}
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series}>
                <defs>
                  <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="sharesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="discGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 10, fill: colors.textMuted }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: colors.textMuted }}
                  axisLine={false}
                  tickLine={false}
                  width={30}
                />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={(l) => l} />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  iconType="circle"
                  iconSize={8}
                />
                <Area
                  type="monotone" dataKey="views" name="Views"
                  stroke="#14b8a6" fill="url(#viewsGrad)" strokeWidth={2}
                />
                <Area
                  type="monotone" dataKey="shares" name="Shares"
                  stroke="#6366f1" fill="url(#sharesGrad)" strokeWidth={2}
                />
                <Area
                  type="monotone" dataKey="discussions" name="Discussions"
                  stroke="#8b5cf6" fill="url(#discGrad)" strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Top Reflections */}
          {data.top_reflections?.length > 0 && (
            <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
              <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: colors.textMuted }}>
                Most Viewed Reflections
              </p>
              <div className="space-y-2">
                {data.top_reflections.map((t, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg px-3 py-2.5"
                    style={{ background: isDark ? 'rgba(15,23,42,0.3)' : '#f8fafc' }}
                    data-testid={`top-reflection-${i}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs font-bold w-5 text-center" style={{ color: colors.accent }}>
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: colors.text }}>
                          {t.topic}
                        </p>
                        <p className="text-[11px]" style={{ color: colors.textMuted }}>
                          {new Date(t.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="flex items-center gap-1 text-xs" style={{ color: '#14b8a6' }}>
                        <Eye className="h-3 w-3" />
                        {t.views}
                      </div>
                      <div className="flex items-center gap-1 text-xs" style={{ color: '#6366f1' }}>
                        <Share2 className="h-3 w-3" />
                        {t.shares}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function AdminReflectionsPage() {
  const { isDark, colors } = useAdminTheme();
  const { hasPermission } = useAdmin();
  const canManage = hasPermission('can_manage_users');

  const [reflections, setReflections] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [backfillStatus, setBackfillStatus] = useState(null); // { pending, total, tagged }
  const [backfilling, setBackfilling] = useState(false);
  const [audience, setAudience] = useState('adult');

  const fetchBackfillStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/reflections/backfill-status`, { withCredentials: true });
      setBackfillStatus(res.data);
    } catch {
      // silently ignore — the button will still be available
    }
  };

  const fetchReflections = async (p = 1, aud = audience) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/reflections?page=${p}&limit=15&audience=${aud}`,
        { withCredentials: true },
      );
      setReflections(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch (err) {
      toast.error('Failed to load reflections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReflections(1, audience); fetchBackfillStatus(); }, [audience]);

  const handleDelete = async (reflectionId) => {
    if (!window.confirm('Delete this reflection and all its shares? This cannot be undone.')) return;
    setDeleting(reflectionId);
    try {
      await axios.delete(`${API}/admin/reflections/${reflectionId}`, { withCredentials: true });
      toast.success('Reflection deleted');
      fetchReflections(page, audience);
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const handleRegenerate = async () => {
    const label = audience === 'adult' ? 'main' : audience;
    if (!window.confirm(`Delete today's ${label} reflection so a fresh one is generated on the next user visit?`)) return;
    setRegenerating(true);
    try {
      await axios.post(
        `${API}/admin/reflections/regenerate?audience=${audience}`,
        {},
        { withCredentials: true },
      );
      toast.success(`Today's ${label} reflection cleared. A new one will generate on next visit.`);
      fetchReflections(page, audience);
    } catch {
      toast.error('Failed to regenerate');
    } finally {
      setRegenerating(false);
    }
  };

  const handleBackfill = async () => {
    const pending = backfillStatus?.pending ?? 0;
    if (pending === 0) {
      toast.info('All reflections are already tagged.');
      return;
    }
    const estSec = Math.ceil(pending * 5);
    if (!window.confirm(
      `Classify ${pending} untagged reflection${pending !== 1 ? 's' : ''} using the LLM? ` +
      `This will take ~${estSec}s and is idempotent.`
    )) return;
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/admin/reflections/backfill-categories`, {}, {
        withCredentials: true,
        timeout: Math.max(60_000, estSec * 1500), // allow plenty of headroom
      });
      const { tagged = 0, failed = 0 } = res.data || {};
      if (failed > 0) {
        toast.warning(`Tagged ${tagged} reflection${tagged !== 1 ? 's' : ''}; ${failed} failed.`);
      } else {
        toast.success(`Tagged ${tagged} reflection${tagged !== 1 ? 's' : ''} successfully.`);
      }
      await fetchBackfillStatus();
      fetchReflections(page);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backfill failed. Check server logs.');
    } finally {
      setBackfilling(false);
    }
  };

  const totalPages = Math.ceil(total / 15);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-reflections-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.text }}>Daily Reflections</h1>
            <p className="text-sm mt-0.5" style={{ color: colors.textMuted }}>
              {total} {audience === 'adult' ? 'main' : audience} reflection{total !== 1 ? 's' : ''} generated
            </p>
          </div>
          {canManage && (
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleBackfill}
                disabled={backfilling || (backfillStatus?.pending === 0)}
                className="rounded-xl px-4 py-2 text-sm font-medium gap-2"
                style={{
                  background: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(5,150,105,0.1)',
                  color: '#10b981',
                  border: `1px solid ${colors.cardBorder}`,
                }}
                data-testid="backfill-categories-btn"
                title={
                  backfillStatus
                    ? `${backfillStatus.pending} pending of ${backfillStatus.total} total`
                    : 'Tag any reflection missing a category'
                }
              >
                <Tags className={`h-4 w-4 ${backfilling ? 'animate-pulse' : ''}`} />
                {backfilling
                  ? 'Tagging...'
                  : backfillStatus?.pending > 0
                    ? `Tag Categories (${backfillStatus.pending})`
                    : 'All Tagged'}
              </Button>
              <Button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="rounded-xl px-4 py-2 text-sm font-medium gap-2"
                style={{
                  background: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(79,70,229,0.1)',
                  color: colors.accent,
                  border: `1px solid ${colors.cardBorder}`,
                }}
                data-testid="regenerate-today-btn"
              >
                <RefreshCw className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Clearing...' : `Regenerate Today's ${audience === 'adult' ? 'Main' : audience.charAt(0).toUpperCase() + audience.slice(1)}`}
              </Button>
            </div>
          )}
        </div>

        {/* Audience tabs — filter reflections by variant */}
        <div
          role="tablist"
          aria-label="Reflection audience"
          className="inline-flex items-center gap-1 rounded-full p-1"
          style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}
          data-testid="admin-audience-tabs"
        >
          {['adult', 'family', 'teen', 'kids'].map((a) => {
            const active = audience === a;
            const label = a === 'adult' ? 'Main' : a.charAt(0).toUpperCase() + a.slice(1);
            return (
              <button
                key={a}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setAudience(a)}
                className="text-xs md:text-sm font-medium rounded-full px-3.5 py-1.5 transition-all"
                style={{
                  background: active ? colors.accent : 'transparent',
                  color: active ? colors.background || '#fff' : colors.textMuted,
                }}
                data-testid={`admin-audience-tab-${a}`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Engagement Chart */}
        <EngagementChart isDark={isDark} colors={colors} />

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBadge icon={BookOpen} label="Total" value={total} color="#14b8a6" isDark={isDark} colors={colors} />
          <StatBadge
            icon={Share2} label="Total Shares"
            value={reflections.reduce((s, r) => s + (r.share_count || 0), 0)}
            color="#6366f1" isDark={isDark} colors={colors}
          />
          <StatBadge
            icon={MessageSquare} label="Discussions"
            value={reflections.reduce((s, r) => s + (r.discussion_count || 0), 0)}
            color="#8b5cf6" isDark={isDark} colors={colors}
          />
          <StatBadge
            icon={Calendar} label="Latest"
            value={reflections[0]?.date?.slice(5) || '—'}
            color="#f59e0b" isDark={isDark} colors={colors}
          />
        </div>

        {/* Reflections List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.textMuted }} />
          </div>
        ) : reflections.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="h-10 w-10 mx-auto mb-3" style={{ color: colors.textMuted, opacity: 0.4 }} />
            <p className="text-sm" style={{ color: colors.textMuted }}>No reflections generated yet.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="reflections-list">
            {reflections.map((r) => {
              const isExpanded = expandedId === r.reflection_id;
              const dateStr = new Date(r.date + 'T12:00:00').toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
              });
              return (
                <div
                  key={r.reflection_id}
                  className="rounded-xl overflow-hidden transition-all"
                  style={{
                    background: isDark ? 'rgba(30,41,59,0.5)' : '#fff',
                    border: `1px solid ${colors.cardBorder}`,
                  }}
                  data-testid={`reflection-item-${r.reflection_id}`}
                >
                  {/* Row header */}
                  <button
                    className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/5 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : r.reflection_id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium" style={{ color: colors.textMuted }}>{dateStr}</span>
                        {r.date === new Date().toISOString().slice(0, 10) && (
                          <span
                            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-semibold"
                            style={{ background: `${colors.accent}20`, color: colors.accent }}
                          >
                            Today
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold truncate" style={{ color: colors.text }}>
                        {r.topic}
                      </p>
                    </div>

                    {/* Badges */}
                    <div className="flex items-center gap-3 shrink-0">
                      {r.share_count > 0 && (
                        <div className="flex items-center gap-1 text-xs" style={{ color: '#6366f1' }}>
                          <Share2 className="h-3.5 w-3.5" />
                          {r.share_count}
                        </div>
                      )}
                      {r.discussion_count > 0 && (
                        <div className="flex items-center gap-1 text-xs" style={{ color: '#8b5cf6' }}>
                          <MessageSquare className="h-3.5 w-3.5" />
                          {r.discussion_count}
                        </div>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" style={{ color: colors.textMuted }} />
                      ) : (
                        <ChevronDown className="h-4 w-4" style={{ color: colors.textMuted }} />
                      )}
                    </div>
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 space-y-4" style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
                      {/* Introduction */}
                      <div className="pt-3">
                        <p className="text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: colors.accent }}>
                          Introduction
                        </p>
                        <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
                          {r.introduction?.replace(/\*\*/g, '')}
                        </p>
                      </div>

                      {/* Body preview */}
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: colors.accent }}>
                          Body
                        </p>
                        <div
                          className="text-sm leading-relaxed whitespace-pre-line"
                          style={{ color: colors.text, maxHeight: 200, overflow: 'hidden' }}
                          dangerouslySetInnerHTML={{
                            __html: r.body
                              ?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              .replace(/\n/g, '<br/>') || ''
                          }}
                        />
                      </div>

                      {/* Thought */}
                      <div
                        className="rounded-lg p-3"
                        style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}
                      >
                        <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: colors.accent }}>
                          Something to Consider
                        </p>
                        <p className="text-sm italic leading-relaxed" style={{ color: colors.text }}>
                          {r.thought_to_consider?.replace(/\*\*/g, '')}
                        </p>
                      </div>

                      {/* Scripture references */}
                      {r.scripture_references?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {r.scripture_references.map((ref, i) => (
                            <span
                              key={i}
                              className="text-[11px] px-2 py-1 rounded-md"
                              style={{
                                background: isDark ? 'rgba(15,23,42,0.5)' : '#f1f5f9',
                                color: colors.textSecondary,
                                border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
                              }}
                            >
                              {ref}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      {canManage && (
                        <div className="flex gap-2 pt-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs gap-1.5 rounded-lg"
                            style={{ color: '#ef4444' }}
                            onClick={() => handleDelete(r.reflection_id)}
                            disabled={deleting === r.reflection_id}
                            data-testid={`delete-reflection-${r.reflection_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            {deleting === r.reflection_id ? 'Deleting...' : 'Delete'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-3 pt-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => fetchReflections(page - 1)}
              style={{ color: colors.textSecondary }}
            >
              Previous
            </Button>
            <span className="text-xs self-center" style={{ color: colors.textMuted }}>
              Page {page} of {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => fetchReflections(page + 1)}
              style={{ color: colors.textSecondary }}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
